/* ==========================================================================
   Gift rewards — custom cart UI on top of the Bogos Free Gift app.

   Bogos stays the engine: it owns the 0€ discount, the "only one gift" rule,
   variant/clone mapping and auto add/remove. This script only RENDERS a custom
   progress bar + gift tiles (Lashboom-style) and delegates the actual add to
   Bogos' documented `addGiftToCartFunc`.

   Data sources (verified against the live store):
   - window.FGSECOMAPP.offers[]          → all tiers incl. locked ones
       .offer_conditions[0].min_amount   → threshold (euros)
       .gift_products[] { title, thumbnail, variants[] { variant_title, ... } }
       .isReachConditions                → unlocked flag
       .giftsAdded[]                     → currently chosen gift(s)
   - window.FGSECOMAPP.SHOPIFY_CART      → cart (prices in cents)
   - document event `fg-gifts:show-slider` { addGiftToCartFunc, gifts[] }
   ========================================================================== */
(function () {
  'use strict';

  var TEXT = {
    heading: 'Sie können sich ein kostenloses Geschenk abholen! 🎁',
    remaining: 'Nur noch {amount} bis zu deinem nächsten Gratis-Geschenk!',
    allUnlocked: 'Super! Du hast alle Geschenke freigeschaltet – wähle dein Lieblingsgeschenk.',
    unlockedTier: 'Freigeschaltet ab {amount} ✓',
    lockedFrom: 'Und schon ab {amount} …',
    lockedRemaining: 'Noch {amount} bis zur Freischaltung',
    choose: 'Gratis auswählen',
    chosen: 'Ausgewählt ✓',
    change: 'Stattdessen wählen',
    chooseVariant: 'Variante wählen'
  };

  var state = {
    addFunc: null,      // Bogos addGiftToCartFunc, captured from the event
    selectedVariant: {} // giftKey -> variantId chosen in the UI (before adding)
  };

  function formatMoney(euros) {
    try {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
        return theme.Currency.formatMoney(Math.round(euros * 100), theme.settings.moneyFormat);
      }
    } catch (e) {}
    return euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  // Short label for the progress milestones: "60 €" (no decimals when whole).
  function formatMilestone(euros) {
    var n = Math.round(euros * 100) / 100;
    var s = (n % 1 === 0)
      ? String(n)
      : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return s + ' €';
  }

  function fg() { return window.FGSECOMAPP; }

  function getOffers() {
    var app = fg();
    if (!app || !app.offers) return [];
    var offers = Array.isArray(app.offers) ? app.offers : Object.values(app.offers);
    return offers.filter(function (o) { return o && o.gift_products && o.gift_products.length; });
  }

  function offerThreshold(offer) {
    var c = offer.offer_conditions && offer.offer_conditions[0];
    return c && c.min_amount ? parseFloat(c.min_amount) : 0;
  }

  function getSubtotalEuros() {
    var app = fg();
    var cart = app && app.SHOPIFY_CART;
    var cents = cart && (cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price);
    if (typeof cents === 'number') return cents / 100;
    return 0;
  }

  function isUnlocked(offer, subtotal) {
    // Bogos' isReachConditions is unreliable/stale; derive from cart subtotal
    // (both subtotal and min_amount are in the same presentment currency).
    return subtotal + 0.001 >= offerThreshold(offer);
  }

  function giftKey(offer, gift, gi) {
    return offer.id + '-' + (gift.original_product_handle || gi);
  }

  // Handles of Bogos clone gifts currently in the cart.
  function cartCloneHandles() {
    var cart = fg() && fg().SHOPIFY_CART;
    var items = (cart && cart.items) || [];
    return items.map(function (i) { return i.handle || ''; });
  }
  // Is THIS gift the one chosen (its clone is in the cart)?
  function isGiftChosen(gift) {
    var h = gift.clone_product_handle;
    return !!h && cartCloneHandles().indexOf(h) !== -1;
  }
  // Has the customer already chosen ANY free gift? (only one allowed overall)
  function anyGiftChosen() {
    return cartCloneHandles().some(function (h) { return /-sca_clone_freegift$/.test(h); });
  }

  // The same gift can appear multiple times in Bogos config; show each once.
  function getGifts(offer) {
    var seen = {};
    return (offer.gift_products || []).filter(function (g) {
      var h = g.original_product_handle || g.title;
      if (seen[h]) return false;
      seen[h] = true;
      return true;
    });
  }

  /* ---- clone variant resolution ---------------------------------------
     Bogos' free gift is a CLONE product priced 0€. We must add the CLONE
     variant id (not the original). Prefer variant_shopify_id; otherwise fetch
     the clone product and match by variant title. Cached per handle. */
  var cloneCache = {};
  function fetchClone(handle) {
    if (cloneCache[handle]) return Promise.resolve(cloneCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) { cloneCache[handle] = p; return p; })
      .catch(function () { return null; });
  }

  function resolveCloneVariantId(gift, variant) {
    // Direct clone variant id on the offer data.
    if (variant && variant.variant_shopify_id) {
      return Promise.resolve(variant.variant_shopify_id);
    }
    // Fall back to the clone product, matched by variant title.
    var handle = gift.clone_product_handle;
    if (!handle) return Promise.resolve(null);
    var wantTitle = variant && (variant.variant_title || variant.title);
    return fetchClone(handle).then(function (p) {
      if (!p || !p.variants || !p.variants.length) return null;
      if (!wantTitle) return p.variants[0].id;
      var match = p.variants.filter(function (v) {
        return (v.title || '').trim() === String(wantTitle).trim();
      })[0];
      return (match || p.variants[0]).id;
    });
  }

  /* ---- add to cart via Bogos ------------------------------------------- */
  function addGift(offer, gift, variant, btn) {
    if (!state.addFunc) {
      console.warn('[gift-rewards] Bogos addGiftToCartFunc not ready yet.');
      if (btn) btn.classList.remove('is-loading');
      return;
    }
    resolveCloneVariantId(gift, variant).then(function (variantId) {
      if (!variantId) {
        console.warn('[gift-rewards] Could not resolve clone variant id for', gift);
        if (btn) btn.classList.remove('is-loading');
        return;
      }
      try {
        state.addFunc(variantId, 1, offer.id);
      } catch (e) {
        console.error('[gift-rewards] addGiftToCartFunc failed', e);
        if (btn) btn.classList.remove('is-loading');
      }
    });
  }

  /* ---- rendering ------------------------------------------------------- */
  function renderVariantPicker(offer, gift, gi) {
    var variants = gift.variants || [];
    if (variants.length <= 1) return '';
    var key = giftKey(offer, gift, gi);
    var sel = state.selectedVariant[key];
    var opts = '<option value=""' + (sel ? '' : ' selected') + ' disabled>' + TEXT.chooseVariant + '</option>';
    opts += variants.map(function (v, vi) {
      var label = (v.variant_title || v.title || ('Variante ' + (vi + 1))).trim();
      return '<option value="' + label.replace(/"/g, '') + '"' + (sel === label ? ' selected' : '') + '>' + label + '</option>';
    }).join('');
    return '<select class="gift-rewards__variant-select" data-gift-variant-select data-key="' + key + '">' + opts + '</select>';
  }

  function renderGiftCard(offer, gift, gi, unlocked) {
    var img = gift.thumbnail || (gift.variants && gift.variants[0] && gift.variants[0].thumbnail) || '';
    var title = (gift.title || '').trim();
    var thisChosen = unlocked && isGiftChosen(gift);
    // Only one gift overall: once one is chosen, the rest look locked.
    var otherChosen = unlocked && !thisChosen && anyGiftChosen();
    var showLock = !unlocked || otherChosen;

    var cls = 'gift-rewards__card';
    if (showLock) cls += ' gift-rewards__card--locked';
    if (thisChosen) cls += ' gift-rewards__card--chosen';

    var html = '<div class="' + cls + '">';
    html += '<div class="gift-rewards__img-wrap">';
    if (img) html += '<img class="gift-rewards__img" src="' + img + '" alt="' + title.replace(/"/g, '') + '" loading="lazy">';
    if (showLock) html += '<span class="gift-rewards__lock" aria-hidden="true">🔒</span>';
    html += '</div>';
    html += '<div class="gift-rewards__title">' + title + '</div>';

    if (thisChosen) {
      html += '<button type="button" class="btn gift-rewards__btn gift-rewards__btn--chosen" data-gift-remove ' +
        'data-offer-id="' + offer.id + '">' + TEXT.chosen + '</button>';
    } else if (unlocked && !otherChosen) {
      html += renderVariantPicker(offer, gift, gi);
      var needsVariant = (gift.variants || []).length > 1 && !state.selectedVariant[giftKey(offer, gift, gi)];
      html += '<button type="button" class="btn gift-rewards__btn" data-gift-add ' +
        'data-offer-id="' + offer.id + '" data-gift-index="' + gi + '"' +
        (needsVariant ? ' disabled' : '') + '>' + TEXT.choose + '</button>';
    }
    html += '</div>';
    return html;
  }

  function renderTier(offer, subtotal) {
    var unlocked = isUnlocked(offer, subtotal);
    var threshold = offerThreshold(offer);
    var cls = 'gift-rewards__tier' + (unlocked ? ' gift-rewards__tier--unlocked' : ' gift-rewards__tier--locked');
    var html = '<div class="' + cls + '">';
    html += '<div class="gift-rewards__tier-head">';
    if (unlocked) {
      html += '<span class="gift-rewards__tier-badge">' + TEXT.unlockedTier.replace('{amount}', formatMoney(threshold)) + '</span>';
    } else {
      var missing = Math.max(0, threshold - subtotal);
      html += '<span class="gift-rewards__tier-label">' + TEXT.lockedFrom.replace('{amount}', formatMoney(threshold)) + '</span>';
      html += '<span class="gift-rewards__tier-missing">' + TEXT.lockedRemaining.replace('{amount}', formatMoney(missing)) + '</span>';
    }
    html += '</div>';
    html += '<div class="gift-rewards__cards">';
    getGifts(offer).forEach(function (gift, gi) {
      html += renderGiftCard(offer, gift, gi, unlocked);
    });
    html += '</div></div>';
    return html;
  }

  function renderProgress(offers, subtotal) {
    var thresholds = offers.map(offerThreshold).filter(function (t) { return t > 0; })
      .sort(function (a, b) { return a - b; });
    if (!thresholds.length) return '';
    var max = thresholds[thresholds.length - 1];
    var fill = Math.min(100, (subtotal / max) * 100);

    var next = thresholds.filter(function (t) { return t > subtotal + 0.001; })[0];
    var msg = next
      ? TEXT.remaining.replace('{amount}', formatMoney(next - subtotal))
      : TEXT.allUnlocked;

    var markers = thresholds.map(function (t) {
      var pos = Math.min(100, (t / max) * 100);
      var reached = subtotal + 0.001 >= t ? ' is-reached' : '';
      return '<span class="gift-rewards__milestone' + reached + '" style="left:' + pos + '%">' +
        '<span class="gift-rewards__milestone-dot" aria-hidden="true">🎁</span>' +
        '<span class="gift-rewards__milestone-label">' + formatMilestone(t) + '</span></span>';
    }).join('');

    return '<div class="gift-rewards__progress-msg">' + msg + '</div>' +
      '<div class="gift-rewards__bar"><div class="gift-rewards__bar-track">' +
      '<div class="gift-rewards__bar-fill" style="width:' + fill + '%"></div>' +
      markers + '</div></div>';
  }

  function render() {
    var mounts = document.querySelectorAll('[data-gift-rewards]');
    if (!mounts.length) return;

    var offers = getOffers().sort(function (a, b) { return offerThreshold(a) - offerThreshold(b); });
    var subtotal = getSubtotalEuros();

    if (!offers.length) {
      mounts.forEach(function (m) { m.setAttribute('hidden', ''); });
      return;
    }

    var html = '<div class="gift-rewards__head">' + TEXT.heading + '</div>';
    html += renderProgress(offers, subtotal);
    html += '<div class="gift-rewards__tiers">';
    offers.forEach(function (offer) { html += renderTier(offer, subtotal); });
    html += '</div>';

    mounts.forEach(function (m) {
      var inner = m.querySelector('[data-gift-rewards-inner]');
      if (inner) inner.innerHTML = html;
      m.removeAttribute('hidden');
    });
  }

  /* ---- events ---------------------------------------------------------- */
  // Remove the currently chosen gift (its clone line) so the customer can switch.
  function removeChosenGift() {
    var cart = fg() && fg().SHOPIFY_CART;
    var items = (cart && cart.items) || [];
    var giftItem = items.filter(function (i) { return /-sca_clone_freegift$/.test(i.handle || ''); })[0];
    if (!giftItem) return;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftItem.key, quantity: 0 })
    }).then(function () {
      lastCount = -1; // force line-item refresh
      syncAll();
    });
  }

  function onChange(e) {
    var sel = e.target.closest('[data-gift-variant-select]');
    if (sel) {
      state.selectedVariant[sel.dataset.key] = sel.value;
      render();
    }
  }

  function onClick(e) {
    var removeBtn = e.target.closest('[data-gift-remove]');
    if (removeBtn) {
      removeBtn.classList.add('is-loading');
      removeChosenGift();
      return;
    }
    var addBtn = e.target.closest('[data-gift-add]');
    if (addBtn) {
      if (addBtn.hasAttribute('disabled')) return;
      var offers = getOffers();
      var offer = offers.filter(function (o) { return String(o.id) === addBtn.dataset.offerId; })[0];
      if (!offer) return;
      var gi = parseInt(addBtn.dataset.giftIndex, 10);
      var gift = getGifts(offer)[gi];
      if (!gift) return;
      var variants = gift.variants || [];
      var variant = variants[0];
      if (variants.length > 1) {
        var chosenTitle = state.selectedVariant[giftKey(offer, gift, gi)];
        variant = variants.filter(function (v) {
          return (v.variant_title || v.title || '').trim() === String(chosenTitle).trim();
        })[0] || variant;
      }
      addBtn.classList.add('is-loading');
      addGift(offer, gift, variant, addBtn);
    }
  }

  /* When Bogos adds/removes a gift line, the theme doesn't know about it, so its
     cart line-item list is stale. Ask the theme to rebuild it — but only when the
     cart item count actually changed, to avoid a rebuild/rescan loop. */
  var lastCount = -1;
  var refreshTimer = null;
  function currentCount() {
    var cart = fg() && fg().SHOPIFY_CART;
    return cart && typeof cart.item_count === 'number' ? cart.item_count : null;
  }
  function refreshThemeCartIfChanged() {
    var c = currentCount();
    if (c != null && c !== lastCount) {
      lastCount = c;
      document.dispatchEvent(new CustomEvent('cart:build'));
    }
  }
  function syncAll() {
    render();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshThemeCartIfChanged, 300);
  }

  function bind() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    // Capture Bogos' add function + eligible gifts, and re-render with our UI.
    document.addEventListener('fg-gifts:show-slider', function (e) {
      if (e.detail && typeof e.detail.addGiftToCartFunc === 'function') {
        state.addFunc = e.detail.addGiftToCartFunc;
      }
      syncAll();
    });

    // Bogos changed the cart (gift added/removed) → re-render + refresh line items.
    document.addEventListener('fg-today-offer:success', syncAll);
    document.addEventListener('fg-today-offer:render', syncAll);
    document.addEventListener('fg-messages:render', syncAll);

    // Theme's own qty change already rebuilds line items; just re-render + sync count.
    document.addEventListener('cart:updated', function () {
      lastCount = currentCount();
      render();
    });
  }

  /* ---- boot: wait for Bogos ------------------------------------------- */
  function boot() {
    bind();
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (getOffers().length) {
        clearInterval(timer);
        lastCount = currentCount();
        render();
      } else if (tries > 60) { // ~15s
        clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
